import { useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { getProductRequest, setLogout } from '../redux/actions/user'
import { getStaticData } from '../redux/actions/staticData'
import { getPersonData, setAvatarFromPersonData } from '../redux/actions/personData'
import { setMessage } from '../redux/actions/message'
import { setLoader } from '../redux/actions/shared'
import { getUserApplications } from '../redux/actions/user'
import { getUserImages, sendCheckUserTier, getRejectedFiles } from '../redux/actions/files'
import { getBase, fetchAndSetIBV } from '../redux/actions/base'
import { t } from 'i18next'
import { notUndefinedNull } from '../helpers/shared'
import { useDispatch } from 'react-redux'
import { USER_LOGIN, USER_APP_ORIGIN, USER_INFO_PRODUCT_REQUEST } from '../redux/types/user'
import { logoBgAppOrigin } from '../constants/appOrigin'
import { useGetOtpTokenMutation, useLoginOtpMutation } from '../redux/services/services'
import { store } from '../redux/store/store'
import { useNavigate } from 'react-router'
import { routes } from '../constants/routes'

export const useOtpModal = ({ setValue, getValues }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [otpValues, setOtpValues] = useState(null)
  const { loading } = useSelector(state => state.base)
  const dispatch = useDispatch()
  const [getOtpToken] = useGetOtpTokenMutation()
  // ? Number of attempts for OTP token before sending a new one.
  // ? Will be probably used inside the useOtpInput custom hook
  const [attemps, setAttemps] = useState(0)

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleOpen = async (receivedValues) => {
    setOtpValues(receivedValues)
    dispatch(setLoader(true));
    try {
      const res = await getOtpToken(receivedValues)
      if(!res.error){
        setIsOpen(true)
        setAttemps(0)
      } else {
        if (res.error){
          let error = res.error.error || res.error.data.errors
          dispatch(setMessage(error, 'error'))
        } 
          
        dispatch(setLoader(false));
        const values = getValues()
        Object.keys(values).forEach(key => {
          setValue(key, '')
        })
      }

    } catch (err) {
      console.error(err)
      if (!loading) dispatch(setLoader(false));
    }
  }

  return {
    isOpen,
    handleClose,
    handleOpen,
    otpValues,
    attemps,
    setAttemps
  }
}


/**
 * Custom hook for the MUIOtpInput component
 * @date 18/3/2024 - 14:19:37
 *
 * @param {{ values; handleClose }} param0
 * @param {Object}   param0.values: loginValues            - Object containing the username and password entered by the user.
 * @param {Function} param0.handleClose: handleCloseModal  - Function that simply handles closing the modal.
 * 
 * @returns {{ 
 *  otp: String; 
 *  handleChange: (newValue: string) => void; 
 *  handleComplete: (value: string) => void; 
 *  otpRef: React.MutableRefObject;
 *  onClose: ({ isLoading }: { isLoading?: boolean; }) => void; 
 * }}
 */
export const useOtpInput = ({ values: loginValues, handleClose: handleCloseModal, attemps, setAttemps }) => {
  const [otp, setOtp] = useState('')
  const dispatch = useDispatch()
  const otpRef = useRef(null)
  const isSubmittingRef = useRef(false)
  const { geolocation, deviceInfo  } = useSelector(state => state.base)
  const { applications, isExpectingNewDataProductRequest, selectedProduct } = useSelector(state => state.user)
  const [loginOtp] = useLoginOtpMutation()

  /**
   * Handles change in otp input.
   * @date 18/3/2024 - 14:11:51
   *
   * @param {String} newValue - OTP-input's value
   */
  const handleChange = (newValue) => {
    if (isSubmittingRef.current) return

    setOtp(newValue)
  }

  /**
   * Handles closing the otp modal.
   * @date 18/3/2024 - 13:55:30
   *
   * @param {{ isLoading?: boolean; }} param0  - Object with options
   * @param {boolean} [param0.isLoading=false] - Boolean that specifies whether or not the otp confirmation is loading.
   */
  const onClose = ({ isLoading = false }) => {
    isSubmittingRef.current = false
    handleCloseModal()
    setOtp('')
    setTimeout(() => {
      if (!isLoading) dispatch(setLoader(false));
    }, 1000);
  }

  const focusFirstOtpInput = () => {
    const firstInput = otpRef.current?.querySelector('input')
    firstInput?.focus()
  }

  const navigate = useNavigate()
  /**
   * Function that triggers on otp input completion.
   * It will attempt to log the user in and redirect to the homepage.
   * @date 18/3/2024 - 14:14:22
   *
   * @async
   * @param {String} value - otp token value
   */
  const handleComplete = async (value) => {
    if (isSubmittingRef.current) return

    isSubmittingRef.current = true
    setOtp('')

    try {
      if(notUndefinedNull(geolocation) && notUndefinedNull(deviceInfo)){
        const values = {
          ...loginValues,
          password: value,
          ip_info: {
            ...deviceInfo,
            ...geolocation
          }
        }
        const res = await loginOtp(values);
        dispatch(setLoader(true));
        if(!res.error && res.data.success){
          onClose({ isLoading: true })
          const {
            token,
            info,
            local_user_info: localUserInfo,
            last_product_request_id: lastProductRequestId
          } = res.data.success

          const appOriginData = await dispatch(getPersonData(info.user_id, lastProductRequestId));

          await dispatch(fetchAndSetIBV(info.user_id))
          if(appOriginData) {
            if (await dispatch(getStaticData())) {
              dispatch({
                type: USER_LOGIN,
                payload: {
                  userIdBifrost: info.user_id,
                  username: info.username,
                  token: token,
                  lastProductRequestId: lastProductRequestId
                }
              });
              await dispatch(getUserImages(info.user_id));
              dispatch(getBase());
              let appOriginFinalData = typeof appOriginData === 'string' ? appOriginData : appOriginData?.appOriginData

              const { code: storeWisenrollCode } = store.getState().user;
              const tierCheckOverrides = {};
              if (!storeWisenrollCode) {
                tierCheckOverrides.bifrost_user_id = info.user_id || res.data.success.bifrost_user_id;
                tierCheckOverrides.code = res.data.success.code
                  || res.data.success.wisenroll_code
                  || localUserInfo?.code;
                tierCheckOverrides.product_request_id = lastProductRequestId;
              }

              if (await dispatch(sendCheckUserTier('', tierCheckOverrides))) {
                dispatch(getRejectedFiles());
                dispatch({ type: USER_APP_ORIGIN, payload: appOriginFinalData });
                dispatch(getProductRequest(localUserInfo.id));
                dispatch(setLoader(false));
              } else {
                dispatch(setLogout());
                dispatch(setLoader(false));
                dispatch(setMessage(t("message.onLogin"), 'error'));
                navigate(routes.login('credinomina'))
              }
              let productsAndServices = [];
              if (!applications) {
                productsAndServices = await dispatch(getUserApplications());
              } else {
                productsAndServices = applications;
              }
              let product = productsAndServices.filter(
                product => product?.application?.unique_name === appOriginFinalData);
              if (product.length === 0) product = [{image_main: logoBgAppOrigin.default }];
              const [productRequest] = product;
              dispatch({ type: USER_INFO_PRODUCT_REQUEST, payload: productRequest });

            }
          }
        } else {
          setTimeout(() => {
            focusFirstOtpInput()
            setOtp('')
            setAttemps(prev => prev + 1)
            setMessage(t("message.onLogin"), 'error')
            dispatch(setLoader(false));
          }, 500);
          if (attemps >= 2) {
            onClose({ isLoading: false })
          }

        }


      } else {
        setMessage(t("message.onLogin"), 'error')
        dispatch(setLoader(false));
      }
      
    } catch (err) {
      onClose({isLoading: false})
      console.error(err)
      dispatch(setLoader(false));
    } finally {
      isSubmittingRef.current = false
    }
   
  }

  return{
    otp,
    handleChange,
    handleComplete,
    otpRef,
    onClose
  }

}

